package fr.frux.feature.map

import androidx.compose.animation.core.Animatable
import androidx.compose.animation.core.AnimationVector1D
import androidx.compose.animation.core.Spring
import androidx.compose.animation.core.spring
import androidx.compose.foundation.gestures.detectVerticalDragGestures
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.outlined.ArrowForward
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material.icons.outlined.Directions
import androidx.compose.material.icons.outlined.Park
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.input.pointer.pointerInput
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import fr.frux.core.ads.NearbyNativeAd
import fr.frux.core.ads.rememberNearbyNativeVisible
import fr.frux.core.designsystem.components.FruxBottomNavDefaults
import fr.frux.core.designsystem.components.FruxButton
import fr.frux.core.designsystem.components.FruxButtonStyle
import fr.frux.core.designsystem.components.FruxDragHandle
import fr.frux.core.designsystem.components.FruxFavoriteButton
import fr.frux.core.designsystem.components.FruxIconButton
import fr.frux.core.designsystem.components.FruxModalBottomSheet
import fr.frux.core.designsystem.components.FruxRemoteImage
import fr.frux.core.designsystem.components.InterceptNavigationBack
import fr.frux.core.designsystem.components.LocalBottomNavPadding
import fr.frux.core.designsystem.components.StatusBadge
import fr.frux.core.designsystem.components.TreeGlyphDisc
import fr.frux.core.designsystem.format.formatDistance
import fr.frux.core.designsystem.icons.glyphFor
import fr.frux.core.designsystem.icons.treeMarkerTint
import fr.frux.core.designsystem.layout.FruxLayoutMode
import fr.frux.core.designsystem.layout.LocalFruxLayoutMode
import fr.frux.core.designsystem.theme.FruxSpacing
import fr.frux.core.designsystem.theme.FruxStatus
import fr.frux.core.maps.Location
import fr.frux.feature.map.resources.Res
import fr.frux.feature.map.resources.map_group_close_description
import fr.frux.feature.map.resources.map_group_subtitle
import fr.frux.feature.map.resources.map_group_title
import fr.frux.feature.map.resources.map_nearby_empty
import fr.frux.feature.map.resources.map_nearby_see_all
import fr.frux.feature.map.resources.map_nearby_title
import fr.frux.feature.map.resources.map_nearby_title_area
import fr.frux.feature.map.resources.map_selected_close_description
import fr.frux.feature.map.resources.map_selected_directions
import fr.frux.feature.map.resources.map_selected_meta_distance
import fr.frux.feature.map.resources.map_selected_meta_distance_unknown
import fr.frux.feature.map.resources.map_selected_meta_with_season
import fr.frux.feature.map.resources.map_selected_start_harvest
import fr.frux.shared.domain.model.HarvestWindow
import fr.frux.shared.domain.model.SeasonalAround
import fr.frux.shared.domain.model.Species
import fr.frux.shared.domain.model.Tree
import fr.frux.shared.domain.model.distanceMetersFrom
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.stringResource
import kotlin.math.roundToInt
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

/** Duree standard des transitions du slot bas ([MapScreen]) — pas de custom easing (piège js). */
internal const val BOTTOM_SLOT_ANIM_MS = 250

/**
 * Delta lu sur un seul callback [detectVerticalDragGestures] au relachement, utilise comme proxy
 * de « velocite franche » (#207 v2) : au-dela de ce seuil, la direction du dernier mouvement prime
 * sur la position pour decider l'ancre, meme si le sheet n'a pas franchi la moitie de sa course.
 * Volontairement pas de vrai `VelocityTracker` — la position + dernier delta signe suffit ici.
 */
private val SHEET_FLING_DRAG_DELTA = 24.dp

/**
 * Fraction de la hauteur totale au-dela de laquelle un drag vers le bas sur [SelectedTreeCard] ou
 * [MarkerGroupSheet] relache la selection plutot que de revenir au repos (#207 v2).
 */
private const val SHEET_DISMISS_FRACTION = 0.35f

/**
 * Offset vertical (px) d'un sheet — vit dans la composition locale de l'ecran, pas dans le
 * ViewModel : ce n'est pas l'ETAT deploye/replie qui, lui, reste toujours pose dans le ViewModel
 * via les callbacks des drag handles ci-dessous (piège Nav3 du repo : un `remember` d'ecran ne
 * survit pas au retour d'onglet, ADR 0009) — seulement sa position visuelle courante pendant et
 * juste apres un geste.
 *
 * v2 (#207) : suit le doigt en continu entre deux ancres plutot que l'ancien retour elastique
 * ±24dp qui sautait d'un coup a la decision — c'est ce defaut d'interaction que Fred a signale.
 */
@Composable
private fun rememberSheetOffset(): Animatable<Float, AnimationVector1D> = remember { Animatable(0f) }

/**
 * Drag vertical vers le bas uniquement, sur la poignee/en-tete de [SelectedTreeCard] et
 * [MarkerGroupSheet] (#207 v2) : le sheet suit le doigt (`coerceAtLeast(0f)`, aucune borne haute).
 * Au relachement au-dela de [SHEET_DISMISS_FRACTION] de sa hauteur mesuree ([sheetHeightPx]) — ou
 * d'un fling franc vers le bas — [onDismiss] efface la selection ; l'`AnimatedContent` du slot bas
 * ([MapScreen]) fait alors le slide de sortie depuis la position atteinte sous le doigt — l'offset
 * n'est pas reinitialise, l'instance entrante a le sien. Sinon, retour au repos par spring.
 */
@Composable
private fun Modifier.sheetDismissDragHandle(
    offset: Animatable<Float, AnimationVector1D>,
    sheetHeightPx: Float,
    onDismiss: () -> Unit,
): Modifier {
    val scope = rememberCoroutineScope()
    return this.pointerInput(sheetHeightPx, onDismiss) {
        val flingPx = SHEET_FLING_DRAG_DELTA.toPx()
        var lastDelta = 0f
        detectVerticalDragGestures(
            onDragStart = { lastDelta = 0f },
            onVerticalDrag = { change, dragAmount ->
                change.consume()
                lastDelta = dragAmount
                scope.launch {
                    offset.snapTo((offset.value + dragAmount).coerceAtLeast(0f))
                }
            },
            onDragEnd = {
                val shouldDismiss =
                    sheetHeightPx > 0f &&
                        (offset.value >= sheetHeightPx * SHEET_DISMISS_FRACTION || lastDelta >= flingPx)
                if (shouldDismiss) {
                    // L'offset n'est PAS remis à 0 : le contenu sortant est déjà hors écran sous
                    // le doigt, et l'AnimatedContent du slot rejoue sa sortie depuis cette
                    // position — le réinitialiser le faisait resurgir une frame à l'ancre avant
                    // de re-glisser (artefact vu à l'émulateur). L'instance entrante a son propre
                    // Animatable, créé à 0.
                    onDismiss()
                } else {
                    scope.launch {
                        offset.animateTo(0f, spring(stiffness = Spring.StiffnessMediumLow))
                    }
                }
            },
            onDragCancel = {
                scope.launch {
                    offset.animateTo(0f, spring(stiffness = Spring.StiffnessMediumLow))
                }
            },
        )
    }
}

/**
 * Feuille de la bannière au repos (#529), même surface que la fiche d'arbre : elle occupe le slot
 * quand rien d'autre ne l'occupe, la fiche prend sa place au toucher d'un arbre. Pas de poignée,
 * rien à faire glisser. Le contenu ([content]) porte l'annonce et sa mention « Annonce ».
 */
@Composable
internal fun MapBannerSheet(content: @Composable () -> Unit) {
    MapSheetSurface(showHandle = false) {
        Box(modifier = Modifier.padding(top = FruxSpacing.sm)) { content() }
    }
}

/**
 * Conteneur commun aux sheets ancrés en bas de la carte (fiche sélectionnée, « autour de toi »,
 * groupe de marqueurs), même forme/couleur que la bottom nav ([FruxBottomNavDefaults]) pour que la
 * jonction se lise comme une seule surface continue — un bottom sheet dont la barre est la base.
 * Pleine largeur (aucun padding horizontal ici : c'est ce qui aligne les bords avec ceux de la
 * barre en dessous), avec une poignée décorative centrée en tête, comme affordance visuelle
 * d'« encore du contenu au-dessus de la barre ».
 *
 * La surface elle-même s'étend jusqu'au bord bas de l'écran, SOUS la bottom nav (#205) : c'est ce
 * qui fait de la barre et du sheet une seule nappe blanche continue plutôt que deux surfaces
 * juxtaposées avec une couture visible. C'est pour ça que le CONTENU, lui, reçoit un padding bas
 * ([LocalBottomNavPadding]) pour rester visible au-dessus de la barre — la surface passe
 * dessous, pas le contenu.
 *
 * L'ombre ([FruxBottomNavDefaults.SheetElevation], portée vers le haut) ne vit que sur cette
 * surface : la barre n'a plus la sienne depuis #205 (elle dessinait une ligne grise sur le sheet
 * qui passe sous elle).
 */
@Composable
private fun MapSheetSurface(
    modifier: Modifier = Modifier,
    onClick: (() -> Unit)? = null,
    // Zone de drag vertical (#207) : appliquee a la bande poignee, plus large que le pictogramme
    // lui-meme pour une cible tactile confortable. Vide par defaut (sheets sans geste de drag).
    handleModifier: Modifier = Modifier,
    showHandle: Boolean = true,
    content: @Composable ColumnScope.() -> Unit,
) {
    // Grand écran : la feuille flotte à distance du bord bas (cf. MapOverlayScaffold), donc ses
    // coins bas s'arrondissent aussi. Sur téléphone elle touche le bord, coins bas droits.
    val expanded = LocalFruxLayoutMode.current == FruxLayoutMode.Expanded
    // Le padding bas du contenu n'a pas le même rôle selon le mode : sur téléphone il dégage la
    // bottom nav sous laquelle la surface passe ; en flottant il n'y a pas de barre (l'hôte
    // fournit 0.dp) et c'est une marge interne ordinaire, sinon les boutons touchent le bord.
    val contentBottomPadding = if (expanded) FruxSpacing.md else LocalBottomNavPadding.current
    val body: @Composable () -> Unit = {
        Column {
            if (showHandle) {
                FruxDragHandle(modifier = handleModifier)
            }
            Column(
                modifier =
                    Modifier
                        .fillMaxWidth()
                        .padding(horizontal = FruxSpacing.md)
                        .padding(bottom = contentBottomPadding),
                content = content,
            )
        }
    }

    val shape =
        if (expanded) {
            FruxBottomNavDefaults.FloatingShape
        } else {
            FruxBottomNavDefaults.Shape
        }
    if (onClick != null) {
        Surface(
            onClick = onClick,
            modifier = modifier.fillMaxWidth(),
            shape = shape,
            color = MaterialTheme.colorScheme.surface,
            shadowElevation = FruxBottomNavDefaults.SheetElevation,
            content = body,
        )
    } else {
        Surface(
            modifier = modifier.fillMaxWidth(),
            shape = shape,
            color = MaterialTheme.colorScheme.surface,
            shadowElevation = FruxBottomNavDefaults.SheetElevation,
            content = body,
        )
    }
}

/**
 * Feuille « autour de toi » (#196 lot 6, refondue par #263). `ModalBottomSheet` natif, comme
 * [MapLegendSheet] et [MapFiltersSheet] : depuis qu'elle n'est plus le fond permanent de l'ecran
 * mais une surface ouverte a la demande depuis [MapListButton], elle appartient a cette famille-la
 * et non aux feuilles du slot bas.
 *
 * Ce que ce choix supprime : la machinerie de drag maison a deux ancres, le bouton de fermeture
 * (le scrim, le geste et le retour systeme s'en chargent) et la question de la cohabitation avec
 * la fiche d'arbre — la modale passe au-dessus de tout.
 *
 * Ce qu'il coute : la carte n'est plus manipulable tant que la feuille est ouverte. Assume — c'est
 * precisement la difference entre une liste qu'on consulte et le fond d'ecran qu'elle etait.
 *
 * Le decompte a disparu de l'en-tete : il ne subsiste que la ou il repond a une vraie question,
 * dans [MarkerGroupSheet]. Les arbres sont tries par distance ([MapState.nearbySorted]).
 */
@OptIn(ExperimentalMaterial3Api::class, ExperimentalTime::class)
@Composable
internal fun NearbyTreesSheet(
    trees: List<Tree>,
    speciesHarvests: Map<Long, List<HarvestWindow>>,
    currentMonth: Int,
    now: Instant,
    reference: Location?,
    aroundUser: Boolean,
    seasonalAround: SeasonalAround,
    onSeasonSpeciesClick: (Species) -> Unit,
    onSeeAllClick: () -> Unit,
    onTreeClick: (Long) -> Unit,
    onDismiss: () -> Unit,
    onRemoveAds: () -> Unit,
    removeAdsLoading: Boolean,
) {
    // Annonce native (#530) : reclamee tant que la feuille est ouverte avec assez d'arbres, inseree
    // seulement une fois prete. Sinon la liste est exactement celle des arbres.
    val nativeReady = rememberNearbyNativeVisible(requested = nearbyNativeRequested(trees.size))
    val listItems = remember(trees, nativeReady) { nearbyListItems(trees, nativeReady) }
    FruxModalBottomSheet(onDismiss = onDismiss, skipPartiallyExpanded = true) {
        Row(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .padding(horizontal = FruxSpacing.md),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Text(
                // Sans position, « Autour de toi » ment : la liste suit la carte, pas la personne (#478).
                text =
                    stringResource(
                        if (aroundUser) Res.string.map_nearby_title else Res.string.map_nearby_title_area,
                    ),
                style = MaterialTheme.typography.titleMedium,
                modifier = Modifier.weight(1f),
            )
            // Seul point d'entree vers l'ecran de liste des arbres depuis #262, qui a retire la
            // rangee de chips ou il vivait. Sa place est ici : cette feuille EST une liste, en
            // voir la suite y est l'action attendue.
            TextButton(onClick = onSeeAllClick) {
                Text(text = stringResource(Res.string.map_nearby_see_all))
                Spacer(modifier = Modifier.width(FruxSpacing.xs))
                Icon(
                    imageVector = Icons.AutoMirrored.Outlined.ArrowForward,
                    contentDescription = null,
                    modifier = Modifier.size(18.dp),
                )
            }
        }

        // En tete de la feuille (#403) : ce que la carte a deja charge dit ce qui est cueillable
        // autour de soi, sans ecran ni requete dedies (ADR 0020).
        SeasonalAroundSection(
            data = seasonalAround,
            onSpeciesClick = onSeasonSpeciesClick,
            showScope = !seasonalAround.isLocal,
            modifier =
                Modifier
                    .fillMaxWidth()
                    .padding(horizontal = FruxSpacing.md, vertical = FruxSpacing.sm),
        )

        if (trees.isEmpty()) {
            NearbyEmptyMessage()
        } else {
            LazyColumn(
                modifier =
                    Modifier
                        .fillMaxWidth()
                        .heightIn(max = NEARBY_SHEET_MAX_HEIGHT),
                contentPadding =
                    PaddingValues(
                        horizontal = FruxSpacing.md,
                        vertical = FruxSpacing.sm,
                    ),
                verticalArrangement = Arrangement.spacedBy(FruxSpacing.xs),
            ) {
                items(listItems, key = { it.key }) { item ->
                    val tree = (item as? NearbyListItem.TreeEntry)?.tree
                    if (tree == null) {
                        NearbyNativeAd(onRemoveAds = onRemoveAds, removeAdsLoading = removeAdsLoading)
                        return@items
                    }
                    NearbyTreeRow(
                        tree = tree,
                        seasonLabel =
                            mapSeasonLabel(
                                speciesHarvests[tree.species.id].orEmpty(),
                                currentMonth,
                            ),
                        status =
                            tree.resolvedStatus(
                                speciesHarvests[tree.species.id].orEmpty(),
                                currentMonth,
                                now,
                            ),
                        reference = reference,
                        onClick = { onTreeClick(tree.id) },
                        leadingIcon =
                            glyphFor(tree.species.scientificName, tree.harvestPart?.name)
                                ?: Icons.Outlined.Park,
                    )
                }
            }
        }
    }
}

/**
 * La feuille s'ouvre sur demande : la refermer aussitot sans rien dire laisserait croire que le
 * bouton n'a pas marche.
 */
@Composable
private fun NearbyEmptyMessage() {
    Text(
        text = stringResource(Res.string.map_nearby_empty),
        style = MaterialTheme.typography.bodyMedium,
        color = MaterialTheme.colorScheme.onSurfaceVariant,
        modifier =
            Modifier
                .fillMaxWidth()
                .padding(horizontal = FruxSpacing.md, vertical = FruxSpacing.lg),
    )
}

/**
 * ~45 % d'un ecran de telephone courant. Une fraction de la hauteur reelle serait plus exacte,
 * mais demanderait de mesurer `BoxWithConstraints` autour de tout l'ecran carte pour un gain
 * marginal — le plafond n'a qu'a eviter que la liste ne masque la carte en entier.
 */
private val NEARBY_SHEET_MAX_HEIGHT = 360.dp

/**
 * Une ligne du sheet deploye. `leadingIcon` par defaut a `Park` ; l'appelant passe le glyphe
 * espece resolu par `glyphFor` (#198) quand il y en a un, teinte pareil (couleur inchangee,
 * seul le vecteur change).
 */
@Composable
private fun NearbyTreeRow(
    tree: Tree,
    seasonLabel: String?,
    status: FruxStatus,
    reference: Location?,
    onClick: () -> Unit,
    leadingIcon: ImageVector = Icons.Outlined.Park,
    // Le groupe de marqueurs passe sa meta a numero stable (toMarkerGroupMeta) : sans lui, deux
    // signalements de meme espece au meme endroit seraient indiscernables dans la liste.
    meta: String = tree.toSelectedMeta(seasonLabel, reference),
) {
    Surface(
        onClick = onClick,
        modifier =
            Modifier
                .fillMaxWidth()
                .heightIn(min = 56.dp),
        shape = MaterialTheme.shapes.medium,
        color = MaterialTheme.colorScheme.surface,
    ) {
        Row(
            modifier = Modifier.padding(FruxSpacing.sm),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
        ) {
            Box(
                modifier =
                    Modifier
                        .size(40.dp)
                        .clip(CircleShape),
                contentAlignment = Alignment.Center,
            ) {
                FruxRemoteImage(
                    url = tree.photoUrl,
                    contentDescription = null,
                    modifier = Modifier.matchParentSize(),
                    // Meme rendu que le marqueur de cet arbre sur la carte (plaque au statut,
                    // glyphe blanc) : la liste et la carte se repondent.
                    fallback = {
                        TreeGlyphDisc(
                            glyph = leadingIcon,
                            tint = treeMarkerTint(tree.species.scientificName, status),
                        )
                    },
                )
            }
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = tree.species.commonNameFr,
                    style = MaterialTheme.typography.titleSmall,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                Text(
                    text = meta,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
            }
            StatusBadge(status = status)
        }
    }
}

/**
 * Choix explicite lorsqu'un groupe ne peut pas etre separe spatialement (coordonnees identiques).
 * Le numero stable rend deux signalements de meme espece distinguables, meme si leur distance et
 * leur statut sont aussi identiques.
 *
 * Non-modal, dans le meme slot bas que [SelectedTreeCard]/[NearbyTreesSheet] (#205) : un
 * `ModalBottomSheet` plein ecran passait par-dessus la bottom nav, incoherent avec les deux autres
 * sheets de cet ecran qui restent au-dessus d'elle. Meme ligne de rangee que [NearbyTreesSheet]
 * ([NearbyTreeRow], vignette glyphe comprise) pour que le remplacement d'un sheet par l'autre ne
 * change pas la forme du contenu, seulement son propos.
 */
@OptIn(ExperimentalTime::class)
@Composable
internal fun MarkerGroupSheet(
    trees: List<Tree>,
    speciesHarvests: Map<Long, List<HarvestWindow>>,
    currentMonth: Int,
    now: Instant,
    reference: Location?,
    onTreeClick: (Tree) -> Unit,
    onDismiss: () -> Unit,
    modifier: Modifier = Modifier,
) {
    // Feuille custom, hors fenêtre de dialogue M3 : le geste de retour doit la fermer comme les
    // feuilles modales (cf. FruxModalBottomSheet).
    InterceptNavigationBack(onBack = onDismiss)
    val dragOffset = rememberSheetOffset()
    var sheetHeightPx by remember { mutableStateOf(0f) }
    MapSheetSurface(
        modifier =
            modifier
                .onSizeChanged { size -> sheetHeightPx = size.height.toFloat() }
                .offset { IntOffset(0, dragOffset.value.roundToInt()) },
        // Drag vers le bas au-dela du seuil → meme sortie que la croix (#207 v2) ; vers le haut,
        // aucune action definie pour ce sheet (pas d'etat deploye/replie a atteindre).
        handleModifier =
            Modifier.sheetDismissDragHandle(
                offset = dragOffset,
                sheetHeightPx = sheetHeightPx,
                onDismiss = onDismiss,
            ),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.Top,
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = stringResource(Res.string.map_group_title),
                    style = MaterialTheme.typography.titleLarge,
                )
                Text(
                    text = stringResource(Res.string.map_group_subtitle, trees.size),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
            }
            FruxIconButton(
                icon = Icons.Outlined.Close,
                contentDescription = stringResource(Res.string.map_group_close_description),
                onClick = onDismiss,
                tint = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }

        Spacer(modifier = Modifier.height(FruxSpacing.sm))

        LazyColumn(
            modifier =
                Modifier
                    .fillMaxWidth()
                    .heightIn(max = NEARBY_SHEET_MAX_HEIGHT),
            verticalArrangement = Arrangement.spacedBy(FruxSpacing.xs),
        ) {
            items(trees, key = { tree -> tree.id }) { tree ->
                NearbyTreeRow(
                    tree = tree,
                    seasonLabel =
                        mapSeasonLabel(
                            speciesHarvests[tree.species.id].orEmpty(),
                            currentMonth,
                        ),
                    status =
                        tree.resolvedStatus(
                            speciesHarvests[tree.species.id].orEmpty(),
                            currentMonth,
                            now,
                        ),
                    reference = reference,
                    onClick = { onTreeClick(tree) },
                    leadingIcon =
                        glyphFor(tree.species.scientificName, tree.harvestPart?.name)
                            ?: Icons.Outlined.Park,
                    meta =
                        tree.toMarkerGroupMeta(
                            mapSeasonLabel(speciesHarvests[tree.species.id].orEmpty(), currentMonth),
                            reference,
                        ),
                )
            }
        }
    }
}

internal fun Tree.toMarkerGroupMeta(
    seasonLabel: String? = null,
    reference: Location? = null,
): String =
    buildString {
        append("Arbre n°")
        append(id)
        distanceMetersFrom(reference)?.let { meters ->
            append(" · à ")
            append(formatDistance(meters))
        }
        seasonLabel?.let {
            append(" · ")
            append(it)
        }
    }

/**
 * Non-modal card for the selected tree, anchored above the bottom nav.
 *
 * [onClose] est la sortie visible de la selection (#127). Retoucher le marqueur ou la pastille
 * referme aussi, mais rien ne l'annonce : la croix est le seul chemin decouvrable, et elle compte
 * double depuis que les actions carte s'escamotent derriere cette fiche (#84).
 *
 * Mise en page : titre a gauche, croix seule a droite, badge de statut sous le texte. Le favori
 * est revenu avec le FavoritesStore (spec 2026-08-12) : l'affordance agit pour de vrai, l'état
 * vient de la source unique.
 */
@Composable
internal fun SelectedTreeCard(
    tree: Tree,
    status: FruxStatus,
    seasonLabel: String?,
    reference: Location?,
    isFavorite: Boolean,
    onFavoriteClick: () -> Unit,
    onCardClick: () -> Unit,
    onStartHarvest: () -> Unit,
    onDirections: () -> Unit,
    onClose: () -> Unit,
    modifier: Modifier = Modifier,
) {
    // Même sortie que la croix : le geste de retour referme la fiche avant de toucher à la pile.
    InterceptNavigationBack(onBack = onClose)
    val dragOffset = rememberSheetOffset()
    var sheetHeightPx by remember { mutableStateOf(0f) }
    MapSheetSurface(
        modifier =
            modifier
                .onSizeChanged { size -> sheetHeightPx = size.height.toFloat() }
                .offset { IntOffset(0, dragOffset.value.roundToInt()) },
        onClick = onCardClick,
        // Meme geste que sur le sheet de groupe (#207 v2) : le bas ferme, le haut ne fait rien.
        handleModifier =
            Modifier.sheetDismissDragHandle(
                offset = dragOffset,
                sheetHeightPx = sheetHeightPx,
                onDismiss = onClose,
            ),
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            verticalAlignment = Alignment.Top,
        ) {
            Box(
                modifier =
                    Modifier
                        .size(40.dp)
                        .clip(CircleShape),
                contentAlignment = Alignment.Center,
            ) {
                FruxRemoteImage(
                    url = tree.photoUrl,
                    contentDescription = null,
                    modifier = Modifier.matchParentSize(),
                    fallback = {
                        TreeGlyphDisc(
                            glyph =
                                glyphFor(tree.species.scientificName, tree.harvestPart?.name)
                                    ?: Icons.Outlined.Park,
                            tint = treeMarkerTint(tree.species.scientificName, status),
                        )
                    },
                )
            }
            Spacer(modifier = Modifier.width(FruxSpacing.sm))
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = tree.species.commonNameFr,
                    style = MaterialTheme.typography.titleLarge,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis,
                )
                Text(
                    text = tree.toSelectedMeta(seasonLabel, reference),
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                )
                // Le badge redevient du contenu, sur sa propre ligne comme dans la maquette
                // `mockups/explore_trees_selected`, et non un troisieme bouton du cluster droit.
                Spacer(modifier = Modifier.height(FruxSpacing.xs))
                StatusBadge(status = status)
            }
            FruxFavoriteButton(
                isFavorite = isFavorite,
                onClick = onFavoriteClick,
            )
            FruxIconButton(
                icon = Icons.Outlined.Close,
                contentDescription = stringResource(Res.string.map_selected_close_description),
                onClick = onClose,
                tint = MaterialTheme.colorScheme.onSurfaceVariant,
            )
        }

        Spacer(modifier = Modifier.height(FruxSpacing.md))

        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.spacedBy(FruxSpacing.sm),
        ) {
            FruxButton(
                text = stringResource(Res.string.map_selected_start_harvest),
                onClick = onStartHarvest,
                style = FruxButtonStyle.Filled,
                modifier = Modifier.weight(1f),
            )
            FruxButton(
                text = stringResource(Res.string.map_selected_directions),
                onClick = onDirections,
                style = FruxButtonStyle.Outline,
                icon = Icons.Outlined.Directions,
                modifier = Modifier.weight(1f),
            )
        }
    }
}

/**
 * Le statut est déjà porté par le StatusBadge à côté ; cette ligne montre la distance, et le
 * libellé de saison s'il y en a un (arbre sans statut déclaré, référentiel disponible).
 *
 * [reference] est la position réelle si elle est connue, sinon le centre interrogé (#260) : le
 * domaine ne porte aucune distance, elle se dérive ici comme partout ailleurs.
 */
@Composable
private fun Tree.toSelectedMeta(
    seasonLabel: String?,
    reference: Location?,
): String {
    val distanceText =
        distanceMetersFrom(reference)?.let {
            stringResource(Res.string.map_selected_meta_distance, formatDistance(it))
        } ?: stringResource(Res.string.map_selected_meta_distance_unknown)
    return seasonLabel
        ?.let { stringResource(Res.string.map_selected_meta_with_season, distanceText, it) }
        ?: distanceText
}
